import { useId, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateWorkspace } from '../hooks/useWorkspaces';

/**
 * Creates a workspace from a name, and enters it.
 *
 * A dialog rather than a form on the page, for the same reason every other
 * create in the product is one: creating a workspace is rare and opening one is
 * what people came to do, so the rare action should not hold the most
 * persistent space on the screen — and a form pinned below the list drifts
 * further down with every workspace added.
 *
 * Navigating on success is the point, not a courtesy: "create a workspace"
 * means "start using one", and leaving the user on the list to find their own
 * new row would make creating and entering two separate chores.
 */
export function CreateWorkspaceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const create = useCreateWorkspace();
  const { error, isPending } = create;

  const [name, setName] = useState('');
  const nameId = useId();
  const errorId = useId();

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !isPending;

  const handleOpenChange = (next: boolean) => {
    // The mutation always resolves in this dialog, so its outcome has somewhere
    // to be shown.
    if (isPending) return;
    onOpenChange(next);
    if (!next) {
      create.reset();
      setName('');
    }
  };

  const submit = () => {
    if (!canSubmit) return;
    create.mutate(trimmed, {
      onSuccess: (workspace) => {
        onOpenChange(false);
        create.reset();
        setName('');
        void navigate({
          to: '/workspaces/$workspaceId',
          params: { workspaceId: workspace.id },
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New workspace</DialogTitle>
          <DialogDescription>
            A workspace is the shared boundary for files, workflows, agents, and
            the tasks that run against them. Name it, and you will be taken
            straight into it.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={nameId}>Name</Label>
            <Input
              id={nameId}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (error !== null) create.reset();
              }}
              aria-invalid={error !== null}
              aria-describedby={error !== null ? errorId : undefined}
              autoComplete="off"
              autoFocus
              placeholder="Payments platform"
            />
          </div>

          {error !== null && (
            <p id={errorId} role="alert" className="text-destructive text-sm">
              {error.message}
            </p>
          )}

          <DialogFooter>
            <DialogClose
              render={<Button variant="outline" disabled={isPending} />}
            >
              Cancel
            </DialogClose>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? 'Creating…' : 'Create workspace'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
